import {createFileRoute, Link} from '@tanstack/react-router';
import {getProfileAttendingEvents} from '@/server/attendance';
import {getProfileRegisteredEvents} from '@/server/registration';
import {getProfileOrganizingEvents} from '@/server/organizer';
import {formatEventDate, daysUntil} from '@/utils/displayFormatUtils';
import {Button} from '@/components/ui/button';
import {Typography} from '@/components/ui/typography';
import {ProfileCalendar} from '@/components/profileCalendar/profileCalendar';
import {CalendarEventList} from '@/components/profileCalendar/calendarEventList';
import {Calendar, MapPin} from 'lucide-react';
import type {CalendarEvent} from '@/apiTypes/calendarEvent';

type ProfileEvent = Awaited<
  ReturnType<typeof getProfileAttendingEvents>
>[number];

/**
 * Merges organizing, registered, and tracked events, deduplicating by event ID.
 * Priority: organizing > registration > tracking.
 */
function mergeCalendarEvents(
  organizing: ProfileEvent[],
  registered: ProfileEvent[],
  attending: ProfileEvent[],
): CalendarEvent[] {
  const byId = new Map<string, CalendarEvent>();

  for (const event of attending) {
    byId.set(event.id, {...event, source: 'tracking'});
  }
  for (const event of registered) {
    byId.set(event.id, {...event, source: 'registration'});
  }
  for (const event of organizing) {
    byId.set(event.id, {...event, source: 'organizing'});
  }

  return Array.from(byId.values());
}

export const Route = createFileRoute('/dashboard/calendar')({
  loader: async () => {
    const [attendingEvents, registeredEvents, organizingEvents] =
      await Promise.all([
        getProfileAttendingEvents(),
        getProfileRegisteredEvents(),
        getProfileOrganizingEvents(),
      ]);
    const calendarEvents = mergeCalendarEvents(
      organizingEvents,
      registeredEvents,
      attendingEvents,
    );
    return {calendarEvents};
  },
  component: CalendarPage,
});

/**
 * Calendar section of the profile dashboard.
 * Shows upcoming events list and a calendar view.
 */
function CalendarPage() {
  const {calendarEvents} = Route.useLoaderData();
  const {featureFlags} = Route.useRouteContext();
  const planToRideEnabled = featureFlags['enable-plan-to-ride'];

  if (!planToRideEnabled) {
    return (
      <div className="text-center py-12">
        <Typography level="B1" className="text-slate-500">
          Calendar coming soon.
        </Typography>
      </div>
    );
  }

  const now = new Date();
  const upcomingEvents = calendarEvents.filter((e) => {
    const relevantDate = e.endDate ?? e.startDate;
    return relevantDate && new Date(relevantDate) >= now;
  });

  return (
    <div className="flex flex-col gap-6">
      <Typography level="H3">Calendar</Typography>

      {/* Mobile: flat list grouped by day */}
      <div className="sm:hidden">
        <CalendarEventList events={upcomingEvents} />
      </div>

      {/* Desktop: grid calendar + upcoming sidebar */}
      <div className="hidden sm:grid lg:grid-cols-[1fr_320px] gap-6">
        <ProfileCalendar events={calendarEvents} />
        <UpcomingEvents events={upcomingEvents} />
      </div>
    </div>
  );
}

interface UpcomingEventsProps {
  events: CalendarEvent[];
}

/**
 * Displays a list of upcoming events the user is attending, registered for,
 * or organizing. Shows an empty state with a link to browse events.
 */
function UpcomingEvents({events}: UpcomingEventsProps) {
  return (
    <div>
      <Typography level="S1" className="mb-3">
        Upcoming Events
      </Typography>
      {events.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
          <Typography level="B2" className="text-slate-500 mb-3">
            No upcoming events yet. Browse events and mark the ones you're
            attending!
          </Typography>
          <Link to="/search">
            <Button variant="outline" size="sm">
              Browse Events
            </Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-2">
          {events.map((event) => (
            <Link
              key={event.id}
              to="/event/$eventId"
              params={{eventId: event.id}}
              className="block"
            >
              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <Typography
                      level="B2"
                      className="font-medium text-slate-900 truncate"
                    >
                      {event.name}
                    </Typography>
                    <div className="flex items-center gap-3 mt-1">
                      {(event.startDate || event.endDate) && (
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <Calendar className="h-3.5 w-3.5" />
                          <Typography level="C2">
                            {formatEventDate(event.startDate, event.endDate)}
                          </Typography>
                        </div>
                      )}
                      {event.location && (
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <MapPin className="h-3.5 w-3.5" />
                          <Typography level="C2" className="truncate">{event.location}</Typography>
                        </div>
                      )}
                    </div>
                  </div>
                  {event.startDate && (
                    <Typography
                      level="C2"
                      className="font-medium text-orange-600 whitespace-nowrap"
                    >
                      {daysUntil(event.startDate)}
                    </Typography>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
